import type { ReminderTitle } from '@cobliteam/api-lib/src/__generated__/graphql';
import { useGetReminderTitles } from '@cobliteam/hooks';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import InputSearch from '@cobliteam/ui/form/InputSearch';
import Label from '@cobliteam/ui/form/Label';
import Icon from '@cobliteam/ui/icons';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectReminderTitleProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectReminderTitle = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectReminderTitleProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [draft, setDraft] = useState(selectedOptions || []);
  const [search, setSearch] = useState<string | undefined>(undefined);

  const { data: titles = [], isLoading } = useGetReminderTitles();

  const hasSameElements = selectedOptions?.length === draft?.length && selectedOptions.every((value) => draft.find((d) => d === value));

  const onClick = useCallback(() => {
    setDraft(selectedOptions || []);
    setSearch(undefined);
  }, [selectedOptions]);

  const onChangeSelect = (titleId: string) => {
    const isSelected = draft.find((d) => d === titleId);
    const updatedSelected = isSelected ? draft.filter((d) => d !== titleId) : [...draft, titleId];
    setDraft(updatedSelected);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setDraft([]);
    setSearch(undefined);
  }, []);

  const handleApply = useCallback(() => {
    onChange(draft);
    handleClose();
  }, [draft, handleClose, onChange]);

  const filterBySearch = useCallback((title: ReminderTitle) => (search ? title.value.toLowerCase().includes(search.toLowerCase()) : true), [search]);

  const filteredTitles = titles.filter(filterBySearch).sort((a, b) => a.value.localeCompare(b.value));

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={draft?.length === 0} onClick={() => setDraft([])}>
              {t('reminder_titles.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Search className='px-2'>
            <InputSearch placeholder={t('reminder_titles.search_placeholder')} value={search} autoFocus debounceInMillis={500} onChange={setSearch} className='w-full' />
          </Dropdown.Search>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={28}
              data={filteredTitles}
              isLoading={isLoading}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item: ReminderTitle) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.id} key={item.id} checked={draft.includes(item.id)} onCheckedChange={() => onChangeSelect(item.id)} />
                  <Label htmlFor={item.id}>{item.value}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('reminder_titles.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('reminder_titles.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' startIcon={<Icon name='wrench' />}>
        {t('reminder_titles.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectReminderTitle;
